/**
 * Printable incident QR codes. Each poster encodes `/qr/<incidentId>/<action>`,
 * which opens the scanner's incident profile with that control ready to tap.
 */

import type { WorkLog } from 'hooks/incident/useWorkLog';

/** `WORK_LOG_ACTIONS` values, kept here so this module stays free of the hooks. */
const ACTION = {
  checkIn: 'check_in',
  checkOut: 'check_out',
  signIn: 'sign_in',
  signOut: 'sign_out',
} as const;

export const QR_ACTIONS = ['check', 'sign'] as const;
export type QrAction = (typeof QR_ACTIONS)[number];

export const NOT_ON_ROSTER_MESSAGE =
  "You are not on this incident's roster. Contact the incident's planning section.";

export function parseQrAction(
  value: string | null | undefined
): QrAction | null {
  return (QR_ACTIONS as readonly string[]).includes(value ?? '')
    ? (value as QrAction)
    : null;
}

export function incidentQrUrl(
  origin: string,
  incidentId: string,
  action: QrAction
): string {
  return `${origin.replace(/\/+$/, '')}/qr/${encodeURIComponent(incidentId)}/${action}`;
}

export function qrProfilePath(incidentId: string, action: QrAction): string {
  return `/iap/${encodeURIComponent(incidentId)}/profile?qr=${action}`;
}

export const QR_POSTER_COPY: Record<
  QrAction,
  { title: string; instruction: string }
> = {
  check: {
    title: 'Check in / Check out',
    instruction:
      'Scan when you arrive at or leave the incident to check in or demobilize.',
  },
  sign: {
    title: 'Sign in / Sign out',
    instruction: 'Scan at the start and end of every shift to sign in or out.',
  },
};

/** The status value each control's change handler expects. */
export type QrNextAction =
  | { label: string; control: 'check'; value: 'checked_in' | 'checked_out' }
  | { label: string; control: 'sign'; value: 'signed_in' | 'signed_out' };

/**
 * The one step the scanned poster offers, following the same available actions
 * the profile's selects honour. Null when nothing is allowed right now.
 */
export function qrNextAction(
  action: QrAction,
  workLog: Pick<WorkLog, 'availableActions'> | null | undefined
): QrNextAction | null {
  const allowed = workLog?.availableActions ?? [];
  if (action === 'check') {
    if (allowed.includes(ACTION.checkIn))
      return { label: 'Check in', control: 'check', value: 'checked_in' };
    if (allowed.includes(ACTION.checkOut))
      return { label: 'Check out', control: 'check', value: 'checked_out' };
    return null;
  }
  if (allowed.includes(ACTION.signIn))
    return { label: 'Sign in', control: 'sign', value: 'signed_in' };
  if (allowed.includes(ACTION.signOut))
    return { label: 'Sign out', control: 'sign', value: 'signed_out' };
  return null;
}

export function qrUnavailableHint(action: QrAction): string {
  return action === 'sign'
    ? 'Signing in or out is not available right now. You may need to be activated and checked in first.'
    : 'Checking in or out is not available right now. You may need to be activated first.';
}
